import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import { type DraftConflict, mergeDraft } from "@/forwards2/deals/draftMerge";
import type {
  DeliveryResponse,
  ProducerPricesRequest,
} from "@/forwards2/forwardsTypes";

/**
 * The producer-prices dialog's draft: the default producer price, each producer's own price and the
 * trade owner, as one flat record of field key to value, so the 409 merge (`draftMerge`) treats each
 * on its own.
 *
 * A price is kept as it was saved or typed — a number input hands back a number once touched — and
 * compared by value (`sameValue`), so a saved `"18.500"` and a typed `18.5` are one price. Null is a
 * blank: a blank producer follows the default producer price, and a blank default is the delivery's
 * own price. Whether a producer has its own price is the backend's to say (`has_own_price`), not read
 * off the figures, so an own price equal to the default stays the producer's own when the default
 * moves. The trade owner is a company id, null for none.
 *
 * A producer field exists for every producer holding a line, and for every one priced before its
 * escrow arrived — saved, or added from the backing pools' members in the dialog. Such a producer
 * without a line is dropped from the draft rather than left blank: blank, it has nothing to say.
 */
export type ProducerPriceValue = string | number | null;
export type ProducerPricesDraft = Record<string, ProducerPriceValue>;

export const TRADE_OWNER_FIELD = "trade_owner";
export const DEFAULT_PRICE_FIELD = "default_price";
const LINE_PREFIX = "line:";
export const lineField = (companyId: number): string =>
  `${LINE_PREFIX}${companyId}`;
export const isLineField = (field: string): boolean =>
  field.startsWith(LINE_PREFIX);
export const companyOfField = (field: string): number =>
  Number(field.slice(LINE_PREFIX.length));

/** A typed or saved value as text, null when blank. */
export const valueText = (
  value: string | number | null | undefined
): string | null => {
  const text = String(value ?? "").trim();
  return text === "" ? null : text;
};

/** Whether two draft values are the same price, or the same trade owner. */
export const sameValue = (
  left: ProducerPriceValue,
  right: ProducerPriceValue
): boolean => {
  const [a, b] = [valueText(left), valueText(right)];
  return a === null || b === null ? a === b : Number(a) === Number(b);
};

/** The companies holding a line in the delivery, as opposed to those priced before their escrow. */
export const linedCompanyIds = (delivery: DeliveryResponse): Set<number> =>
  new Set(
    delivery.producer_lines
      .filter((line) => line.has_line)
      .map((line) => line.company_id)
  );

/** The delivery's saved prices and trade owner, in the draft's terms: a producer without its own price reads blank. */
export function savedProducerPrices(
  delivery: DeliveryResponse
): ProducerPricesDraft {
  const draft: ProducerPricesDraft = {
    [DEFAULT_PRICE_FIELD]: delivery.default_producer_price,
    [TRADE_OWNER_FIELD]: delivery.trade_owner?.company_id ?? null,
  };
  for (const line of delivery.producer_lines)
    draft[lineField(line.company_id)] = line.has_own_price
      ? line.unit_price
      : null;
  return draft;
}

/**
 * The request that saves the draft: the default and the own prices, stated whole, the trade owner,
 * and the `plan_version` the draft was composed against. Every producer field carrying a price is
 * sent as typed, even one equal to the default; a blank one is left out, so it follows the default.
 */
export function producerPricesRequest(
  draft: Readonly<ProducerPricesDraft>,
  planVersion: number
): ProducerPricesRequest {
  const defaultPrice = valueText(draft[DEFAULT_PRICE_FIELD]);
  const overrides: Record<number, string> = {};
  for (const [field, value] of Object.entries(draft)) {
    const price = valueText(value);
    if (isLineField(field) && price !== null)
      overrides[companyOfField(field)] = price;
  }
  const tradeOwner = draft[TRADE_OWNER_FIELD];
  return {
    plan_version: planVersion,
    overrides,
    default_price: defaultPrice,
    trade_owner_company_id: typeof tradeOwner === "number" ? tradeOwner : null,
  };
}

/**
 * The draft after a 409: merged onto the delivery as it is saved now (`mergeDraft`).
 *
 * @property shipped - The delivery shipped meanwhile: its prices are history, so the dialog turns
 *   read-only on the saved values and nothing of the draft survives.
 * @property takenOver - How many fields now hold a saved value the user did not type, a line that
 *   arrived meanwhile included.
 * @property linesRemoved - How many of the user's producers left the delivery meanwhile: a line
 *   gone, or someone else removed a price set before escrow.
 */
export interface ProducerPricesMerge {
  draft: ProducerPricesDraft;
  conflicts: Record<string, DraftConflict<ProducerPriceValue>>;
  takenOver: number;
  linesRemoved: number;
  shipped: boolean;
}

/**
 * `mergeDraft` knows only the fields saved now, so the producer fields only the user has — those
 * added from the picker, and those removed — are settled here: a removed one merges as a blank,
 * and an added one is kept unless someone else priced the same producer meanwhile.
 */
export function mergeProducerPrices(
  base: Readonly<ProducerPricesDraft>,
  draft: Readonly<ProducerPricesDraft>,
  latest: DeliveryResponse
): ProducerPricesMerge {
  const theirs = savedProducerPrices(latest);
  const shipped = isDeliveryFrozen(latest.transaction_status);
  const removedByMe = Object.keys(base).filter(
    (field) => isLineField(field) && !(field in draft)
  );
  const mine: ProducerPricesDraft = {
    ...draft,
    ...Object.fromEntries(removedByMe.map((field) => [field, null])),
  };
  const merged = mergeDraft({
    base,
    theirs,
    mine,
    locked: shipped ? new Set(Object.keys(theirs)) : undefined,
    equals: sameValue,
  });

  const values = { ...merged.values };
  const conflicts = { ...merged.conflicts };
  const takenOver = new Set(merged.takenOver);
  const added = Object.keys(draft).filter(
    (field) => isLineField(field) && !(field in base)
  );
  for (const field of shipped ? [] : added) {
    takenOver.delete(field);
    const saved = theirs[field];
    if (valueText(saved ?? null) === null) values[field] = draft[field];
    else if (!sameValue(draft[field], saved)) {
      values[field] = draft[field];
      conflicts[field] = { mine: draft[field], theirs: saved };
    }
  }

  const lined = linedCompanyIds(latest);
  for (const [field, value] of Object.entries(values)) {
    const blankWithoutLine =
      isLineField(field) &&
      !lined.has(companyOfField(field)) &&
      valueText(value) === null;
    if (blankWithoutLine && !(field in conflicts)) {
      delete values[field];
      takenOver.delete(field);
    }
  }

  return {
    draft: values,
    conflicts,
    takenOver: takenOver.size,
    linesRemoved: merged.removed.filter((field) => !removedByMe.includes(field))
      .length,
    shipped,
  };
}
